'use client'

import {useCallback, useLayoutEffect, useMemo, useRef, useState, type ReactNode} from 'react'
import {usePathname} from 'next/navigation'
import {PublicEntryChromeContext} from './PublicEntryChromeContext'
import {PublicOfferTabs, type PublicOfferPage} from './PublicOfferTabs'
import {PublicSiteShell} from './PublicSiteShell'
import {resolvePublicSitePage} from './publicSiteNavigation'

export function PublicEntryChrome({children, fitroUrl, partnerName, showAboutPage, showCourses, showEvents, showPrivateBookings}: {
    children: ReactNode
    fitroUrl: string
    partnerName: string
    showAboutPage: boolean
    showCourses: boolean
    showEvents: boolean
    showPrivateBookings: boolean
}) {
    const pathname = usePathname()
    const page = resolvePublicSitePage(pathname)
    const isOfferPage = page === 'schedule' || page === 'courses' || page === 'events' || page === 'privateBookings'
    const contentRef = useRef<HTMLDivElement>(null)
    const previousPathname = useRef(pathname)
    // Layout effect so the fade starts before the new page paints at full opacity.
    useLayoutEffect(() => {
        const changed = previousPathname.current !== pathname
        previousPathname.current = pathname
        const content = contentRef.current
        if (!changed || !isOfferPage || !content || typeof content.animate !== 'function') return
        const animation = content.animate([{opacity: 0}, {opacity: 1}], {duration: 160, easing: 'cubic-bezier(0.23, 1, 0.32, 1)'})
        return () => animation.cancel()
    }, [isOfferPage, pathname])
    const isDetail = /^\/(?:zajecia|wydarzenia|kursy)\//.test(pathname)
    const [redirects, setRedirects] = useState<{url: string}[]>([])
    const registerRedirect = useCallback((url: string) => {
        const entry = {url}
        setRedirects(current => [...current, entry])
        return () => setRedirects(current => current.filter(item => item !== entry))
    }, [])
    const [offerPages, setOfferPages] = useState<{page: PublicOfferPage}[]>([])
    const registerOfferPage = useCallback((offerPage: PublicOfferPage) => {
        const entry = {page: offerPage}
        setOfferPages(current => [...current, entry])
        return () => setOfferPages(current => current.filter(item => item !== entry))
    }, [])
    const chrome = useMemo(() => ({registerRedirect, registerOfferPage}), [registerOfferPage, registerRedirect])
    const activeOfferPage = (isDetail ? offerPages.at(-1)?.page : undefined) ?? page as PublicOfferPage
    return (
        <PublicSiteShell activePage={isOfferPage ? 'schedule' : page} fitroUrl={fitroUrl} partnerName={partnerName}
            redirectUrl={redirects.at(-1)?.url ?? '/home'} showAboutPage={showAboutPage}>
            {isOfferPage && (showCourses || showEvents || showPrivateBookings) ? (
                <div className="pt-7">
                    <PublicOfferTabs activePage={activeOfferPage} showCourses={showCourses}
                        showEvents={showEvents} showPrivateBookings={showPrivateBookings}
                        compactBottomSpacing/>
                </div>
            ) : null}
            <PublicEntryChromeContext.Provider value={chrome}>
                <div ref={contentRef} data-public-entry-content>
                    {children}
                </div>
            </PublicEntryChromeContext.Provider>
        </PublicSiteShell>
    )
}
